import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DatePicker } from "antd";
import moment from "moment/moment";
import axios from "axios";
import {
  BsSearch,
  BsCalendar3,
  BsXCircle,
  BsHourglassSplit,
  BsArrowRepeat,
  BsBoxSeam,
  BsEye,
  BsFileEarmarkText,
  BsThreeDotsVertical,
  BsClipboard,
  BsFilter,
  BsDownload,
  BsSortDown,
} from "react-icons/bs";
import { get_Order_list_v2, get_Order_stats_v2 } from "../../../services/authServices";
import { TICKES_DOWNLOAD } from "../../../constant/api";
import "./orderListV2.css";

const { RangePicker } = DatePicker;

const BOOKING_STATUS_OPTIONS = [
  "INITIATED",
  "CREATED",
  "PENDING",
  "IN PROCESS",
  "PAID",
  "SUCCESS",
  "FAILED",
  "CANCELLED",
  "ABORTED",
];
const PAYMENT_STATUS_OPTIONS = ["SUCCESS", "PENDING", "FAILED", "IN PROCESS"];
const TRIP_TYPE_OPTIONS = ["DOMESTIC", "INTERNATIONAL"];
const SEARCH_TYPE_OPTIONS = ["ONEWAY", "RETURN"];
const CABIN_CLASS_OPTIONS = ["ECONOMY", "PREMIUM_ECONOMY", "BUSINESS", "FIRST"];
const BOOKING_TYPE_OPTIONS = ["REFUNDABLE", "NON REFUNDABLE", "PARTIALLY REFUNDABLE"];
const FARE_TYPE_OPTIONS = ["REGULAR", "SENIOR CITIZEN"];
const PLATFORM_OPTIONS = ["WEB", "MOBILE", "OFFLINE"];
const PAYMENT_GATEWAY_OPTIONS = ["RAZORPAY", "PHONEPE"];

// Filters with real backend support this pass. Airline/Airport/Refund Status/Fare
// Range are shown (matching the design's layout) but disabled — see
// docs/FLIGHT_ORDER_ADMIN_V2_BLUEPRINT.md for why they're deferred.
const CORE_FILTERS = [
  { key: "bookingStatus", label: "Booking Status", options: BOOKING_STATUS_OPTIONS },
  { key: "paymentStatus", label: "Payment Status", options: PAYMENT_STATUS_OPTIONS },
  { key: "tripType", label: "Trip Type", options: TRIP_TYPE_OPTIONS },
  { key: "searchType", label: "Search Type", options: SEARCH_TYPE_OPTIONS },
  { key: "cabinClass", label: "Cabin Class", options: CABIN_CLASS_OPTIONS },
  { key: "bookingType", label: "Booking Type", options: BOOKING_TYPE_OPTIONS },
  { key: "fareType", label: "Fare Type", options: FARE_TYPE_OPTIONS },
  { key: "platform", label: "Platform", options: PLATFORM_OPTIONS },
  { key: "paymentGateway", label: "Payment Gateway", options: PAYMENT_GATEWAY_OPTIONS },
];
const COMING_SOON_FILTERS = ["Airline", "Airport", "Refund Status", "Fare Range"];

const QUICK_FILTERS = [
  { key: "today", label: "Today's Orders" },
  { key: "last7", label: "Last 7 Days" },
  { key: "last30", label: "Last 30 Days" },
  { key: "upcoming", label: "Upcoming Trips" },
  { key: "departureToday", label: "Today's Departure" },
  { key: "arrivalToday", label: "Today's Arrival" },
];

const EMPTY_FILTERS = {
  bookingStatus: "",
  paymentStatus: "",
  tripType: "",
  searchType: "",
  cabinClass: "",
  bookingType: "",
  fareType: "",
  platform: "",
  paymentGateway: "",
  search: "",
  dateFrom: "",
  dateTo: "",
  upcomingTrips: false,
  departureToday: false,
  arrivalToday: false,
};

const money = (value) => (value == null ? "-" : `₹${Number(value).toLocaleString("en-IN")}`);
const dateTime = (value) => (value ? moment(value).format("DD MMM, hh:mm A") : "-");
const durationLabel = (mins) => {
  if (mins == null) return "-";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h}h ${String(m).padStart(2, "0")}m`;
};
const journeyTypeLabel = (journeyType) => (journeyType === "ONEWAY" ? "ONE WAY" : journeyType || "-");
const initialsOf = (passenger) => {
  if (!passenger) return "?";
  const initials = `${passenger.firstName?.[0] ?? ""}${passenger.lastName?.[0] ?? ""}`.toUpperCase();
  return initials || "?";
};

function bookingStatusVariant(status) {
  if (status === "SUCCESS") return "success";
  if (["CANCELLED", "ABORTED", "FAILED"].includes(status)) return "danger";
  if (status) return "warning";
  return "muted";
}
function paymentStatusVariant(label) {
  if (label === "SUCCESS") return "success";
  if (/fail/i.test(label ?? "")) return "danger";
  if (label) return "warning";
  return "muted";
}

function getPageNumbers(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const keep = new Set([1, 2, total - 1, total, current - 1, current, current + 1]);
  const sorted = [...keep].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);
  const result = [];
  let prev = null;
  for (const p of sorted) {
    if (prev !== null && p - prev > 1) result.push("…");
    result.push(p);
    prev = p;
  }
  return result;
}

const StatCard = ({ icon, colorClass, label, value, sub }) => (
  <div className="ov2-stat-card">
    <div className={`ov2-stat-icon ${colorClass}`}>{icon}</div>
    <div>
      <div className="ov2-stat-label">{label}</div>
      <div className="ov2-stat-value">{value}</div>
      {sub && <div className="ov2-stat-sub">{sub}</div>}
    </div>
  </div>
);

const Badge = ({ variant = "neutral", children }) => (
  <span className={`ov2-badge ov2-badge--${variant}`}>{children}</span>
);

const OrderListV2 = () => {
  const navigate = useNavigate();

  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [sort, setSort] = useState({ sortBy: "bookingDate", sortDir: "desc" });
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [activeQuickFilter, setActiveQuickFilter] = useState(null);
  const [showFilters, setShowFilters] = useState(true);
  const [ordersLoading, setOrdersLoading] = useState(false);

  const fetchStats = async () => {
    try {
      const res = await get_Order_stats_v2();
      setStats(res?.data?.Data ?? null);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchOrders = async (nextPage, nextPerPage, nextFilters, nextSort) => {
    setOrdersLoading(true);
    try {
      const res = await get_Order_list_v2({
        page: nextPage,
        limit: nextPerPage,
        sortBy: nextSort.sortBy,
        sortDir: nextSort.sortDir,
        ...nextFilters,
      });
      const data = res?.data?.Data;
      setOrders(data?.list ?? []);
      setTotalCount(data?.totalCount ?? 0);
    } catch (e) {
      console.error(e);
    } finally {
      setOrdersLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchOrders(1, perPage, EMPTY_FILTERS, sort);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyQuickFilter = (key) => {
    const today = moment().format("YYYY-MM-DD");
    const reset = { dateFrom: "", dateTo: "", upcomingTrips: false, departureToday: false, arrivalToday: false };
    let updates = reset;
    if (key === "today") updates = { ...reset, dateFrom: today, dateTo: today };
    else if (key === "last7") updates = { ...reset, dateFrom: moment().subtract(6, "days").format("YYYY-MM-DD"), dateTo: today };
    else if (key === "last30") updates = { ...reset, dateFrom: moment().subtract(29, "days").format("YYYY-MM-DD"), dateTo: today };
    else if (key === "upcoming") updates = { ...reset, upcomingTrips: true };
    else if (key === "departureToday") updates = { ...reset, departureToday: true };
    else if (key === "arrivalToday") updates = { ...reset, arrivalToday: true };

    const nextFilters = { ...filters, ...updates };
    setFilters(nextFilters);
    setActiveQuickFilter(key);
    setPage(1);
    fetchOrders(1, perPage, nextFilters, sort);
  };

  const clearAll = () => {
    setFilters(EMPTY_FILTERS);
    setActiveQuickFilter(null);
    setPage(1);
    fetchOrders(1, perPage, EMPTY_FILTERS, sort);
  };

  const handleDateRangeChange = (dates) => {
    const nextFilters = {
      ...filters,
      dateFrom: dates?.[0] ? moment(dates[0].$d).format("YYYY-MM-DD") : "",
      dateTo: dates?.[1] ? moment(dates[1].$d).format("YYYY-MM-DD") : "",
      upcomingTrips: false,
      departureToday: false,
      arrivalToday: false,
    };
    setFilters(nextFilters);
    setActiveQuickFilter(null);
    setPage(1);
    fetchOrders(1, perPage, nextFilters, sort);
  };

  const handleFilterChange = (field) => (e) => {
    const nextFilters = { ...filters, [field]: e.target.value };
    setFilters(nextFilters);
    setPage(1);
    fetchOrders(1, perPage, nextFilters, sort);
  };

  const handleSearchChange = (e) => {
    setFilters((f) => ({ ...f, search: e.target.value }));
  };
  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      setPage(1);
      fetchOrders(1, perPage, filters, sort);
    }
  };

  const handleSortByChange = (e) => {
    const nextSort = { ...sort, sortBy: e.target.value };
    setSort(nextSort);
    fetchOrders(page, perPage, filters, nextSort);
  };
  const toggleSortDir = () => {
    const nextSort = { ...sort, sortDir: sort.sortDir === "asc" ? "desc" : "asc" };
    setSort(nextSort);
    fetchOrders(page, perPage, filters, nextSort);
  };

  const goToPage = (p) => {
    setPage(p);
    fetchOrders(p, perPage, filters, sort);
  };
  const handlePerPageChange = (e) => {
    const n = Number(e.target.value);
    setPerPage(n);
    setPage(1);
    fetchOrders(1, n, filters, sort);
  };

  const handleDownloadInvoice = async (orderId) => {
    try {
      const response = await axios.post(TICKES_DOWNLOAD, { orderId }, { responseType: "blob" });
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${orderId}.pdf`;
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (e) {
      console.error(e);
    }
  };

  const copyText = (text) => {
    if (text) navigator.clipboard?.writeText(text);
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / perPage));
  const rangeStart = totalCount === 0 ? 0 : (page - 1) * perPage + 1;
  const rangeEnd = Math.min(totalCount, page * perPage);

  return (
    <div className="ov2-page">
      <div className="ov2-header">
        <div>
          <h1 className="ov2-header-title">Flight Orders</h1>
          <div className="ov2-breadcrumb">Dashboard &gt; Flight Orders</div>
        </div>
        <div className="ov2-header-actions">
          <button className="ov2-btn ov2-btn--outline" disabled title="Coming soon">
            <BsDownload /> Export
          </button>
          <button className="ov2-btn ov2-btn--primary" onClick={() => setShowFilters((v) => !v)}>
            <BsFilter /> Advanced Filters
          </button>
        </div>
      </div>

      <div className="ov2-stats-row">
        <StatCard
          icon={<BsBoxSeam />}
          colorClass="ov2-stat-icon--purple"
          label="Total Orders"
          value={stats ? stats.totalOrders.toLocaleString("en-IN") : "—"}
          sub="All time"
        />
        <StatCard
          icon={<BsHourglassSplit />}
          colorClass="ov2-stat-icon--orange"
          label="Pending"
          value={stats ? stats.pending.count : "—"}
          sub={stats ? `${stats.pending.percentOfTotal}% of total` : ""}
        />
        <StatCard
          icon={<BsXCircle />}
          colorClass="ov2-stat-icon--red"
          label="Cancelled"
          value={stats ? stats.cancelled.count : "—"}
          sub={stats ? `${stats.cancelled.percentOfTotal}% of total` : ""}
        />
        <StatCard
          icon={<BsArrowRepeat />}
          colorClass="ov2-stat-icon--purple"
          label="Refunds"
          value={stats ? stats.refunds.count : "—"}
          sub={stats ? `${stats.refunds.percentOfTotal}% of total` : ""}
        />
      </div>

      <div className="ov2-search-row">
        <div className="ov2-search-box">
          <BsSearch />
          <input
            placeholder="Search by Order ID, User ID, Merchant Order ID..."
            value={filters.search}
            onChange={handleSearchChange}
            onKeyDown={handleSearchKeyDown}
          />
        </div>
        <RangePicker
          allowClear
          value={
            filters.dateFrom && filters.dateTo
              ? [moment(filters.dateFrom), moment(filters.dateTo)]
              : null
          }
          onChange={handleDateRangeChange}
          format={["DD/MM/YYYY", "DD/MM/YYYY"]}
          placeholder={["Booking date from", "to"]}
          suffixIcon={<BsCalendar3 />}
        />
      </div>

      <div className="ov2-quick-filters">
        <span className="ov2-muted">Quick Filters:</span>
        {QUICK_FILTERS.map((qf) => (
          <button
            key={qf.key}
            className={`ov2-pill ${activeQuickFilter === qf.key ? "ov2-pill--active" : ""}`}
            onClick={() => applyQuickFilter(qf.key)}
          >
            {qf.label}
          </button>
        ))}
        <button className="ov2-clear-all" onClick={clearAll}>
          <BsXCircle /> Clear All
        </button>
      </div>

      {showFilters && (
        <div className="ov2-filter-grid">
          {CORE_FILTERS.map((f) => (
            <div className="ov2-filter-item" key={f.key}>
              <label>{f.label}</label>
              <select className="form-select" value={filters[f.key]} onChange={handleFilterChange(f.key)}>
                <option value="">All</option>
                {f.options.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>
          ))}
          {COMING_SOON_FILTERS.map((label) => (
            <div className="ov2-filter-item ov2-filter-item--disabled" key={label}>
              <label>
                {label} <span className="ov2-coming-soon">Soon</span>
              </label>
              <select className="form-select" disabled>
                <option>All</option>
              </select>
            </div>
          ))}
        </div>
      )}

      <div className="ov2-table-toolbar">
        <div>
          {totalCount === 0
            ? "No orders found"
            : `Showing ${rangeStart} to ${rangeEnd} of ${totalCount} orders`}
        </div>
        <div className="ov2-table-toolbar-right">
          <div className="d-flex align-items-center gap-2">
            <span>Sort By</span>
            <select className="form-select form-select-sm" value={sort.sortBy} onChange={handleSortByChange}>
              <option value="bookingDate">Booking Date</option>
              <option value="orderId">Order ID</option>
            </select>
            <button className="ov2-icon-btn" onClick={toggleSortDir} title={`Sort ${sort.sortDir}ending`}>
              <BsSortDown style={sort.sortDir === "asc" ? { transform: "scaleY(-1)" } : undefined} />
            </button>
          </div>
          <div className="d-flex align-items-center gap-2">
            <span>Rows per page</span>
            <select className="form-select form-select-sm" value={perPage} onChange={handlePerPageChange}>
              {[10, 25, 50, 100].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
          <div className="ov2-pagination">
            <button className="ov2-page-btn" disabled={page === 1} onClick={() => goToPage(page - 1)}>
              ‹
            </button>
            {getPageNumbers(page, totalPages).map((p, idx) =>
              p === "…" ? (
                <span key={`ellipsis-${idx}`} className="ov2-muted px-1">
                  …
                </span>
              ) : (
                <button
                  key={p}
                  className={`ov2-page-btn ${p === page ? "ov2-page-btn--active" : ""}`}
                  onClick={() => goToPage(p)}
                >
                  {p}
                </button>
              )
            )}
            <button className="ov2-page-btn" disabled={page === totalPages} onClick={() => goToPage(page + 1)}>
              ›
            </button>
          </div>
        </div>
      </div>

      <div className="ov2-table-wrap">
        <table className="ov2-table">
          <thead>
            <tr>
              <th>Order Details</th>
              <th>Passenger</th>
              <th>Journey</th>
              <th>Travel Date</th>
              <th>Payment</th>
              <th>Fare</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {ordersLoading && (
              <tr>
                <td colSpan={8} className="ov2-empty-state">
                  Loading…
                </td>
              </tr>
            )}
            {!ordersLoading && orders.length === 0 && (
              <tr>
                <td colSpan={8} className="ov2-empty-state">
                  No orders match these filters.
                </td>
              </tr>
            )}
            {!ordersLoading &&
              orders.map((order) => (
                <tr key={order.orderId}>
                  <td>
                    <div className="ov2-order-id">
                      {order.orderId}{" "}
                      <Badge variant="neutral">{journeyTypeLabel(order.journeyType)}</Badge>
                    </div>
                    <div className="ov2-muted">{order.userId}</div>
                    <div className="ov2-muted">Invoice: {order.orderInvoice ?? "-"}</div>
                    <div className="ov2-muted">
                      <BsCalendar3 /> {dateTime(order.bookingDate)}
                    </div>
                  </td>

                  <td>
                    <div className="ov2-passenger-cell">
                      <div className="ov2-avatar">{initialsOf(order.primaryPassenger)}</div>
                      <div>
                        <div>
                          {order.primaryPassenger
                            ? `${order.primaryPassenger.title ?? ""} ${order.primaryPassenger.firstName ?? ""} ${order.primaryPassenger.lastName ?? ""}`
                            : "-"}
                        </div>
                        <div className="ov2-muted">{order.contact?.mobile ?? "-"}</div>
                        <div className="ov2-muted">{order.contact?.email ?? "-"}</div>
                        <Badge variant="neutral">
                          {(order.paxCount?.adult ?? 0) + (order.paxCount?.child ?? 0) + (order.paxCount?.infant ?? 0)}{" "}
                          Passenger(s)
                        </Badge>
                      </div>
                    </div>
                  </td>

                  <td>
                    {order.journey ? (
                      <>
                        <div>
                          {order.journey.airlineName} ({order.journey.airlineCode})
                        </div>
                        <div className="ov2-journey-route">
                          {order.journey.waypoints.map((code, idx) => (
                            <React.Fragment key={idx}>
                              {idx > 0 && <span>→</span>}
                              <span>{code}</span>
                            </React.Fragment>
                          ))}
                        </div>
                        <div className="ov2-muted">
                          {order.journey.totalStops === 0
                            ? "Non Stop"
                            : `${order.journey.totalStops} Stop${order.journey.totalStops > 1 ? "s" : ""}${
                                order.journey.stopCities.length ? ` (${order.journey.stopCities.join(", ")})` : ""
                              }`}
                        </div>
                      </>
                    ) : (
                      <span className="ov2-muted">-</span>
                    )}
                  </td>

                  <td>
                    <div>{dateTime(order.departure)}</div>
                    <div className="ov2-muted">↓</div>
                    <div>{dateTime(order.arrival)}</div>
                    <Badge variant="neutral">Duration: {durationLabel(order.durationMin)}</Badge>
                  </td>

                  <td>
                    <div>{order.payment?.gateway ?? "-"}</div>
                    <div className="ov2-muted">{order.payment?.method ?? "-"}</div>
                    <Badge variant={paymentStatusVariant(order.payment?.status)}>
                      {order.payment?.status ?? "-"}
                    </Badge>
                    {order.payment?.paymentId && (
                      <div className="ov2-muted">
                        {order.payment.paymentId}{" "}
                        <button
                          className="ov2-icon-btn"
                          style={{ width: 20, height: 20 }}
                          onClick={() => copyText(order.payment.paymentId)}
                          title="Copy payment ID"
                        >
                          <BsClipboard size={11} />
                        </button>
                      </div>
                    )}
                  </td>

                  <td>
                    <div className="ov2-order-id">{money(order.fare?.totalFare)}</div>
                    <div className="ov2-muted">Base Fare: {money(order.fare?.baseFare)}</div>
                    <div className="ov2-muted">Tax &amp; Fees: {money(order.fare?.taxAndFee)}</div>
                    {order.offer && (
                      <>
                        <div style={{ color: "#1e9e4e" }}>Offer: -{money(order.offer.amountOff)}</div>
                        <div className="ov2-muted">You Save: {money(order.offer.youSave)}</div>
                      </>
                    )}
                  </td>

                  <td>
                    <div>
                      <Badge variant={bookingStatusVariant(order.bookingStatus)}>{order.bookingStatus ?? "-"}</Badge>
                    </div>
                    {order.bookingStatusLabel && order.bookingStatusLabel !== order.bookingStatus && (
                      <div className="mt-1">
                        <Badge variant="info">{order.bookingStatusLabel}</Badge>
                      </div>
                    )}
                    <div className="ov2-muted mt-1">Refund: {order.refundStatus ?? "N/A"}</div>
                  </td>

                  <td>
                    <div className="ov2-actions-cell">
                      <button
                        className="ov2-icon-btn"
                        title="View order"
                        onClick={() => navigate(`/orderDetailsList/${order.orderId}`)}
                      >
                        <BsEye />
                      </button>
                      <button
                        className="ov2-icon-btn"
                        title="Download invoice"
                        onClick={() => handleDownloadInvoice(order.orderId)}
                      >
                        <BsFileEarmarkText />
                      </button>
                      <button className="ov2-icon-btn" disabled title="Coming soon">
                        <BsThreeDotsVertical />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default OrderListV2;
